/**
 * Pane model scaffold — phase 4.2 groundwork (DESIGN-phase4.md §6).
 *
 * A pane is split into exactly two records keyed by the same id:
 *  - PaneView (in the store): the serializable facts. This is ALSO the
 *    layout-persistence format — saving a layout is serializing the slice.
 *  - PaneRuntime (in the registry here): live handles + hot data. NEVER
 *    serialized, NEVER snapshotted — snapshotting handles alongside facts
 *    is precisely how the historical pane0-aliasing bugs happened.
 *
 * The page-side rewrite of setLayout()/createPane() onto this model is the
 * main 4.2 step and intentionally NOT part of this scaffold.
 */

export type LayoutName = "single" | "2h" | "2v" | "2plus1" | "grid4";

export interface PaneView {
  id: string;
  symbol: string;
  timeframe: string;
  selectedDate: string | null;
  chartType: string;
}

export interface PanesSlice {
  layout: LayoutName;
  /** id of the pane global accessors redirect to (DESIGN §6). */
  active: string;
  panes: PaneView[];
}

/** Live handles + hot data for one pane. Opaque types on purpose — the
 * registry never inspects them, it only owns their lifecycle. */
export interface PaneRuntime {
  chart: unknown;
  candleSeries: unknown;
  volumeSeries: unknown;
  ws: { close(): void } | null;
  containerEl: unknown;
  allCandles: unknown[];
  timers: Record<string, ReturnType<typeof setInterval> | undefined>;
}

export interface PaneRuntimeRegistry {
  create(id: string, runtime: PaneRuntime): void;
  get(id: string): PaneRuntime | undefined;
  /** Close socket, clear timers, drop the record. Idempotent. */
  destroy(id: string): void;
  /** destroy() every pane not in `keep` — the setLayout teardown primitive. */
  retainOnly(keep: string[]): void;
  ids(): string[];
}

export function createPaneRuntimeRegistry(): PaneRuntimeRegistry {
  const runtimes = new Map<string, PaneRuntime>();

  function destroy(id: string): void {
    const rt = runtimes.get(id);
    if (!rt) return;
    runtimes.delete(id);
    try {
      rt.ws?.close();
    } catch {
      /* already dead */
    }
    for (const key of Object.keys(rt.timers)) {
      const t = rt.timers[key];
      if (t != null) clearInterval(t);
      rt.timers[key] = undefined;
    }
  }

  return {
    create(id, runtime) {
      if (runtimes.has(id)) destroy(id);
      runtimes.set(id, runtime);
    },
    get: (id) => runtimes.get(id),
    destroy,
    retainOnly(keep) {
      const keepSet = new Set(keep);
      for (const id of Array.from(runtimes.keys())) {
        if (!keepSet.has(id)) destroy(id);
      }
    },
    ids: () => Array.from(runtimes.keys()),
  };
}
